/*
 * ModelPicker component for use creating and updating subscription model information.
 * 
 * This takes the following props:
 *   auth      - auth object
 *   mode      - one of 'add' or 'edit'
 *   appModel  - an object containing app model and app data
 * 
 */

import React from 'react';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';

import { Accordion, AccordionSummary, AccordionDetails, Box, Button, Checkbox, FormControlLabel, Grid, MenuItem,
         Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { Add, Check, ExpandMore, HelpOutline, LinkOutlined, Password, SettingsAccessibility } from '@mui/icons-material';

import AbEnvironment from '../auth/AbEnvironment';
import AppBindStyles from '../../AppBindStyles';
import PercentageTextField from '../ui/PercentageTextField';

const abEnvironment = new AbEnvironment();

const styleSheet = theme => ({
    tabroot: {
        flexGrow: 1,
        margin: 0,
        width: '100%',
        borderBottom: '2px solid #dedede'
    },
    tabScrollButton: {
        flex: '0 0 0px'
    },
    groupBoxAuthOnly: {
        marginTop: -3,
        border: "1px solid",
        borderTop: "0px",
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingTop: theme.spacing(0),
        paddingBottom: theme.spacing(1),
        borderTopRightRadius: "0px",
        borderTopLeftRadius: "0px",
        borderBottomRightRadius: "4px",
        borderBottomLeftRadius: "4px",
        borderColor: theme.palette.divider,
    },
    groupBoxAuthOnlySeparate: {
        marginTop: 0,
        border: "1px solid",
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        paddingTop: theme.spacing(0),
        paddingBottom: theme.spacing(1),
        borderTopRightRadius: "4px",
        borderTopLeftRadius: "4px",
        borderBottomRightRadius: "4px",
        borderBottomLeftRadius: "4px",
        borderColor: theme.palette.divider,
    },
    groupBoxCustomerPay: {
        marginTop: -3,
        border: "1px solid",
        borderTop: "0px",
        padding: theme.spacing(2),
        //paddingTop: theme.spacing(2),
        borderTopRightRadius: "0px",
        borderTopLeftRadius: "0px",
        borderBottomRightRadius: "4px",
        borderBottomLeftRadius: "4px",
        borderColor: theme.palette.divider,
    },    
    groupBoxCredentials: {
        marginTop: 8,
        marginBottom: 8,
    },
    groupBoxCredentialsAccordion: {
        marginTop: -8,
        paddingTop: 0,
        paddingBottom: 8,
    },
    groupBoxInvitation: {
        marginTop: 8,
        marginBottom: 8,
        padding: theme.spacing(1),
    },
    aoCheckBox: {
        paddingTop: 4,
        paddingBottom: 4,
        paddingLeft: 8,
    },
    instructionTitle: {
        width: 400,
        minWidth: 260,
    },
    instructionDescription: {
        width: 600,
        minWidth: 260,
    },
    linkName: {
        width: 180,
        minWidth: 180,
    },
    linkUrl: {
        width: 360,
        minWidth: 360,
    },
    linkIcon: {
        color: theme.palette.secondary.main,
    },    
    adHocEmail: {
        width: 220,
        minWidth: 220,
    },
    adHocButton: {
        minWidth: 32,
        width: 32,
    },
    addButton: {
        marginTop: theme.spacing(1),
        marginRight: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    cancelButton: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    percentageTextField: {
        width: 65,
        minWidth: 65,
        textAlign: 'right',
    },
    markupDescription: {
        marginTop: -4, // Used to create narrower vertical spacing between title and description
    }
});
  
class ModelPicker extends React.Component {
    state = {
        paymentOrder: null,
        taskType: null,

        // expense to customer fields
        wholesaleDiscountPercentage: null,
        appMarkupPercentage: null,

        // instruction
        instructionTitle: null,
        instructionText: null,
        linkName: null,
        linkUrl: null,

        // checkboxes for auth information
        askForEmail: true,
        askForPassword: true,
        askForUsername: false,
        askForDomain: false,
        askForPhone: false,

        // checkboxes for invitation information
        inviteEmails: null,
        adHocEmail: null,

        // auth information expanded state
        isAuthExpanded: false
    };

    /**
     * Set initial state from passed app props
     */
    componentDidMount() {
        let theAppModel = this.props.appModel;

        // Handle rValues on payment order and app markup percentage
        let thePaymentOrder = theAppModel.paymentOrder;
        if (theAppModel.rPaymentOrder != null) {
            thePaymentOrder = theAppModel.rPaymentOrder;
        }

        let theAppMarkupPercentage = theAppModel.appMarkupPercentage;
        if (theAppModel.rAppMarkupPercentage != null) {
            theAppMarkupPercentage = theAppModel.rAppMarkupPercentage;
        }

        let theStateObj = {
            paymentOrder:                 thePaymentOrder,
            taskType:                     theAppModel.taskType,
            wholesaleDiscountPercentage:  theAppModel.wholesaleDiscountPercentage,
            appMarkupPercentage:          theAppMarkupPercentage,
            instructionTitle:             theAppModel.instructionTitle,
            instructionText:              theAppModel.instructionText,
            linkName:                     theAppModel.linkName,
            linkUrl:                      theAppModel.linkUrl,
            askForEmail:                  theAppModel.askForEmail,
            askForPassword:               theAppModel.askForPassword,
            askForUsername:               theAppModel.askForUsername,
            askForDomain:                 theAppModel.askForDomain,
            askForPhone:                  theAppModel.askForPhone,
            inviteEmails:                 theAppModel.inviteEmails
        }
        this.setState(theStateObj);
    }

    returnStateValuesToParent = () => {
        return this.state;
    }

    handleAccordionChange = (e, isExpanded) => {
        this.setState({isAuthExpanded: isExpanded});
    }

    handleTabChange = (event, value) => {
        //let theNum = parseInt(event.target.value, 10);
        if (value === 0) {
            this.setState({taskType: 0, instructionTitle: '', instructionText: '', linkName: '', linkUrl: ''});
        } else if (value === 1) {
            this.setState({taskType: 1, instructionTitle: '', instructionText: '', linkName: 'Instructions', linkUrl: ''});
        }
    }

    handlePaymentOrderChange = (newPaymentOrder) => {
        let stateReplacement = {};

        stateReplacement.paymentOrder = newPaymentOrder;
        if (newPaymentOrder === 0) {
            if (this.props.appModel.wholesaleDiscountPercentage != null) {
                stateReplacement.wholesaleDiscountPercentage = this.props.appModel.wholesaleDiscountPercentage;
            }
            if (this.props.appModel.appMarkupPercentage != null) {
                stateReplacement.appMarkupPercentage = this.props.appModel.appMarkupPercentage;
            }
        } else if (newPaymentOrder === 2) {
            stateReplacement.wholesaleDiscountPercentage = 0;
            stateReplacement.appMarkupPercentage = 0;
        } else if (newPaymentOrder === 4) {
            // Changing to auth only.  If state values aren't set here, put in some defaults.
            if (this.state.taskType == null) {
                stateReplacement.taskType = 0;
            }

            if (this.state.taskType == null || this.state.taskType === 0) {            
                if (this.state.instructionTitle == null) {
                    stateReplacement.instructionTitle = '';
                }
                if (this.state.instructionText == null) {
                    stateReplacement.instructionText = '';
                }
                if (this.state.linkName == null) {
                    stateReplacement.linkName = '';
                }
                if (this.state.linkUrl == null) {
                    stateReplacement.linkUrl = '';
                }
            }
        }
        this.setState(stateReplacement);
        this.props.updateModel(newPaymentOrder);
    }

    toggleStateValue = name => event => {
        let newStateValue = true;
        if (this.state[name] === true) {
            newStateValue = false;
        }
        this.setState({[name]: newStateValue});
    }

    toggleInviteValue = (theInviteEmails, inviteEmailIndex) => {
        if (theInviteEmails.length > inviteEmailIndex) {
            theInviteEmails[inviteEmailIndex].checked = !theInviteEmails[inviteEmailIndex].checked;
        }
        this.setState({inviteEmails: theInviteEmails});
    }

    updateWholesaleDiscountPercentage = (newPercentage) => {
        this.setState({wholesaleDiscountPercentage: newPercentage});
        this.props.updateWdp(newPercentage);
    }

    updateAppMarkupPercentage = (newPercentage) => {
        this.setState({appMarkupPercentage: newPercentage});
        this.props.updateAmp(newPercentage);
    }

    getErrorMessage(field, minValue, maxValue) {
        let errMesg = null;
    
        let value = Number(field); 
        if (value < minValue) {
            return 'Cannot be less than ' + minValue;
        }
        if (value > maxValue) {
            return 'Cannot be greater than ' + maxValue;
        }
    
        return errMesg;
    }
    
    areFieldsInError = () => {
        if (this.getErrorMessage(this.state.appMarkupPercentage, -100, 900) !== null) {
          return true;
        }
        if (this.getErrorMessage(this.state.wholesaleDiscountPercentage, 0, 100) !== null) {
          return true;
        }
        return false;
    }

    isAdHocEmailValid(theInviteEmails) {
        const { validateEmail } = this.props.auth;
        if (this.state.adHocEmail == null || this.state.adHocEmail === '') {
            return false;
        }

        // Is email an email address?
        let emailOk = validateEmail(this.state.adHocEmail);
        if (! emailOk) {
            return false;
        }

        // Is the email already in the list?
        for (let i=0; i<theInviteEmails.length; i++) {
            if (theInviteEmails[i].email === this.state.adHocEmail) {
                return false;
            }
        }

        return true;
    }

    addAdHocEmail(theInviteEmails) {
        if (this.state.adHocEmail != null && this.state.adHocEmail !== '') {
            let newEmail = {
                email: this.state.adHocEmail,
                checked: true
            }
            theInviteEmails.push(newEmail);

            this.setState({inviteEmails: theInviteEmails, adHocEmail: ''});
        }
    }
        
    render() {
        const classes = this.props.classes;

        //
        // Expensing options are now controlled by a feature flag.  If expsensing is not active, we only
        // want to allow a user to select "Request access" (4) or "I will sign up" (5).
        let expensingActive = false;
        let expensing = abEnvironment.getFlagValue("expensing");
        if (expensing != null && expensing.flagValue === 1) {
          expensingActive = true;
        }

        //
        // If a subscription already exists (legacy expense subscription), we should show it, but not allow any changes.  We'll
        // control that by not allowing existing expense subscriptions to be editable if the feature flag is not set.  So the only
        // way we should get into the model picker is for new subscriptions, or for existing subscriptions that should be
        // editable.
        //
        // We also want to adjust the model defaults in the catalog appropriately.  However, if expensing is inactive, but the default
        // order is an expense type (0,1, or 2), adjust it to 5.
        //
        // Overall, payment order adjustments:
        // - If it is null, set it to empty string, which we'll use as the value for "not set", and indicate that it
        //   needs selection.
        //
        // - Treat payment order 1 as 0
        //
        // - If expensing is not active, treat payment order 0, 1, and 2 to 5
        //
        let thePaymentOrder = this.state.paymentOrder;
        if (thePaymentOrder === 1) {
            thePaymentOrder = 0;
        } else if (thePaymentOrder == null) {
            thePaymentOrder = 'NA';
        }

        if (expensingActive === false) {
            if (thePaymentOrder === 0 || thePaymentOrder === 1 || thePaymentOrder === 2) {
                thePaymentOrder = 5;
            }
        }

        let wholesaleTooltip = ` Some software vendors sell partners discounted subscriptions. You in turn  then mark the subscriptions up and sell it to your customers.
        \n\r
        It is important to show only the retail price for the subscription to your customer. Any discounts should come from you, which you can add on the next line below.`

        // For now, showing wholesale discount for all apps.  We anticipate
        // overriding this on an app by app basis in the future.
        let showWholesaleDiscount = true;

        // let customerLabel = this.props.usingAccountName + ' will pay directly';

        //
        // Falling through here means that an app has been selected
        //

        let expenseToCustomerTitle = 'Expense to customer';
        let expenseToCustomerDescription = 'Automatically expense this subscription to my customer after I sign up on their behalf.';
        let expenseToMeTitle = 'I will pay';
        let expenseToMeDescription = 'I will cover the cost of the subscription as part of the project.';
        let requestAccessTitle = 'Request access';
        let requestAccessDescription = 'Ask my customer to share their password or invite me into their subscription.';
        let signUpTitle = "I will sign up";
        let signUpDescription = "I will sign up for this subscription on behalf of my customer.";

        let attributesToShare = [];
        if (this.state.askForEmail    === true) { attributesToShare.push('email'); }
        if (this.state.askForPassword === true) { attributesToShare.push('password'); }
        if (this.state.askForUsername === true) { attributesToShare.push('username'); }
        if (this.state.askForDomain   === true) { attributesToShare.push('subdomain'); }
        if (this.state.askForPhone    === true) { attributesToShare.push('phone'); }

        let credentialLine = "Ask my customer to share their ";
        for (let i=0; i<attributesToShare.length; i++) {
            if (i > 0) {
                if (i === attributesToShare.length - 1) {
                    if (attributesToShare.length > 2) {
                        credentialLine = credentialLine + ',';
                    }
                    credentialLine = credentialLine + ' and ';
                } else {
                    credentialLine = credentialLine + ', ';
                }
            }
            credentialLine = credentialLine + attributesToShare[i];
        }

        let authOnlyAccordionHelper = 'Options';
        if (this.state.isAuthExpanded === true) {
            authOnlyAccordionHelper = 'Options';
        }

        // We're making a copy of the state invitedEmails array such that it can be mutable
        let theInviteEmails = [];
        if (this.state.inviteEmails != null) {
            // Copy the state array
            for (let i=0; i<this.state.inviteEmails.length; i++) {
                let anEmail = this.state.inviteEmails[i];
                let anEmailObj = {email: anEmail.email, checked: anEmail.checked};
                theInviteEmails.push(anEmailObj);
            }
        }
        // console.log('model picker - theInviteEmails is ' + JSON.stringify(theInviteEmails));

        let inviteEmailIndex = -1;

        let authOnlyClass = classes.groupBoxAuthOnly;
        if (this.props.mode === 'edit') {
            authOnlyClass = classes.groupBoxAuthOnlySeparate;
        }

        // If there are values for terms, use the rValue, and if that isn't set, use the value
        let theWholesaleDiscountPercentage = this.state.wholesaleDiscountPercentage;
        let theAppMarkupPercentage = this.state.appMarkupPercentage;

        return (
            <Grid container justifyContent="flex-start" alignItems="center" spacing={0}>
                {this.props.mode === 'add' &&
                <React.Fragment>
                    {/* Use text field select convenience with "Sharing model" label */}
                    {thePaymentOrder !== 'NA' &&
                    <Grid item xs={12} style={{marginTop: 16}}>
                        <TextField
                            value={thePaymentOrder}
                            onChange={(e) => { this.handlePaymentOrderChange(e.target.value); } } 
                            label="Sharing model"
                            select
                            fullWidth
                            autoFocus
                            variant="outlined"
                        >
                            <MenuItem value={4}>
                                <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body1" style={AppBindStyles.boldLabel}>{requestAccessTitle}</Typography>
                                    </Grid>                  
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body2">{requestAccessDescription}</Typography>
                                    </Grid>
                                </Grid>
                            </MenuItem>
                            <MenuItem value={5}>
                                <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body1" style={AppBindStyles.boldLabel}>{signUpTitle}</Typography>
                                    </Grid>                  
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body2">{signUpDescription}</Typography>
                                    </Grid>
                                </Grid>
                            </MenuItem>
                            {expensingActive === true &&
                            <MenuItem value={0}>
                                <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body1" style={AppBindStyles.boldLabel}>{expenseToCustomerTitle}</Typography>
                                    </Grid>                  
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body2">{expenseToCustomerDescription}</Typography>
                                    </Grid>
                                </Grid>
                            </MenuItem>}
                            {expensingActive === true &&
                            <MenuItem value={2}>
                                <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body1" style={AppBindStyles.boldLabel}>{expenseToMeTitle}</Typography>
                                    </Grid>                  
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body2">{expenseToMeDescription}</Typography>
                                    </Grid>
                                </Grid>
                            </MenuItem>}
                        </TextField>
                    </Grid>}

                    {/* When payment order is not selected, make the label the instruction */}
                    {thePaymentOrder === 'NA' &&
                    <Grid item xs={12} style={{marginTop: 16}}>
                        <TextField
                            value={thePaymentOrder}
                            onChange={(e) => { this.handlePaymentOrderChange(e.target.value); } } 
                            label="Sharing model"
                            select
                            fullWidth
                            autoFocus
                            variant="standard"
                        >
                            <MenuItem value="NA" disabled>
                                <Typography align="left" variant="body1">Select a sharing model</Typography>
                            </MenuItem>
                            <MenuItem value={4}>
                                <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body1" style={AppBindStyles.boldLabel}>{requestAccessTitle}</Typography>
                                    </Grid>                  
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body2">{requestAccessDescription}</Typography>
                                    </Grid>
                                </Grid>
                            </MenuItem>
                            <MenuItem value={5}>
                                <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body1" style={AppBindStyles.boldLabel}>{signUpTitle}</Typography>
                                    </Grid>                  
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body2">{signUpDescription}</Typography>
                                    </Grid>
                                </Grid>
                            </MenuItem>
                            {expensingActive === true &&
                            <MenuItem value={0}>
                                <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body1" style={AppBindStyles.boldLabel}>{expenseToCustomerTitle}</Typography>
                                    </Grid>                  
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body2">{expenseToCustomerDescription}</Typography>
                                    </Grid>
                                </Grid>
                            </MenuItem>}
                            {expensingActive === true &&
                            <MenuItem value={2}>
                                <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body1" style={AppBindStyles.boldLabel}>{expenseToMeTitle}</Typography>
                                    </Grid>                  
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body2">{expenseToMeDescription}</Typography>
                                    </Grid>
                                </Grid>
                            </MenuItem>}
                        </TextField>
                    </Grid>}
                </React.Fragment>}

                {this.props.mode === 'edit' &&
                <React.Fragment>
                    {(thePaymentOrder === 0 || thePaymentOrder === 2) &&
                    <Grid item xs={12} style={{marginTop: 16}}>
                        <TextField
                            value={thePaymentOrder}
                            onChange={(e) => { this.handlePaymentOrderChange(e.target.value); } } 
                            label="Sharing model"
                            select
                            fullWidth
                            autoFocus
                            variant="outlined"
                        >
                            <MenuItem value={0}>
                                <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body1" style={AppBindStyles.boldLabel}>{expenseToCustomerTitle}</Typography>
                                    </Grid>                  
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body2">{expenseToCustomerDescription}</Typography>
                                    </Grid>
                                </Grid>
                            </MenuItem>
                            <MenuItem value={2}>
                                <Grid container spacing={0}>
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body1" style={AppBindStyles.boldLabel}>{expenseToMeTitle}</Typography>
                                    </Grid>                  
                                    <Grid item xs={12}>
                                        <Typography align="left" variant="body2">{expenseToMeDescription}</Typography>
                                    </Grid>
                                </Grid>
                            </MenuItem>
                        </TextField>
                    </Grid>}
                </React.Fragment>}

                {/* Settings for customer paid subscriptions */}
                { (thePaymentOrder === 1 || thePaymentOrder === 0) &&
                <Grid item xs={12}>
                    <Box className={classes.groupBoxCustomerPay}>
                        <Grid container spacing={1} justifyContent="space-between" alignItems="flex-start">
                            {showWholesaleDiscount &&
                            <React.Fragment>
                                <Grid item>
                                    <Grid container direction="column" spacing={0}>
                                        <Grid item>
                                            <Typography variant="body1" style={AppBindStyles.boldLabel}>If you are buying <u>{this.props.appModel.appName}</u> with a partner discount, enter it here</Typography>
                                        </Grid>
                                        <Grid item style={AppBindStyles.markupDescription}> 
                                            <Typography variant="caption">(e.g. if you’re charged $80 at a 20% discount, we’ll show the customer a retail price of $100)</Typography>
                                            <Tooltip placement='bottom' title={wholesaleTooltip}>
                                                <HelpOutline fontSize="small" style={{verticalAlign: "-16%", marginLeft: '4px'}}/>
                                            </Tooltip>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item>
                                    <PercentageTextField 
                                        id="wholesale-discount-percentage"
                                        className={classes.percentageTextField}
                                        value={theWholesaleDiscountPercentage}
                                        showErrMsg={true}
                                        allowNegative={false}
                                        error={this.getErrorMessage(theWholesaleDiscountPercentage, 0, 100)}
                                        errorMsg={this.getErrorMessage(theWholesaleDiscountPercentage, 0, 100)}
                                        minValue={0}
                                        maxValue={100}
                                        minEnterValue={0}
                                        maxEnterValue={9999}
                                        onChange={(e) => this.updateWholesaleDiscountPercentage(e.target.value)}
                                        textAlign="right"
                                    />
                                </Grid>
                            </React.Fragment>}
                            <Grid item xs={12}/>
                            <Grid item>
                                <Grid container direction="column" spacing={0}>
                                    <Grid item>
                                        <Typography variant="body1" style={AppBindStyles.boldLabel}>Add your management fee or discount</Typography>
                                    </Grid>
                                    <Grid item style={AppBindStyles.markupDescription}> 
                                        <Typography variant="caption">Add a management fee (e.g. 20%) or a discount (e.g. -10%)</Typography>
                                        <Tooltip placement='bottom' title={'On top of the retail price, you can add your own management fee or discount.'}>
                                            <HelpOutline fontSize="small" style={{verticalAlign: "-16%", marginLeft: '4px'}}/>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item>
                                <PercentageTextField 
                                    id="app-markup-percentage" 
                                    className={classes.percentageTextField}
                                    value={theAppMarkupPercentage}
                                    allowNegative={true}
                                    showErrMsg={true}
                                    error={this.getErrorMessage(theAppMarkupPercentage, -100, 900)}
                                    errorMsg={this.getErrorMessage(theAppMarkupPercentage, -100, 900)}
                                    minValue={-100}
                                    maxValue={900}
                                    minEnterValue={-9999}
                                    maxEnterValue={9999}
                                    onChange={ (e) => {this.updateAppMarkupPercentage(e.target.value)} }
                                    textAlign="right"
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>}

                {/* Settings for auth-only subscriptions */}
                { thePaymentOrder === 4 &&
                <Grid item xs={12}>
                    <Box className={authOnlyClass}>
                        <Grid container spacing={0} justifyContent="flex-start" alignItems="center">
                            <Grid item xs={12}>
                                <Tabs
                                    value={this.state.taskType}
                                    onChange={this.handleTabChange}
                                    indicatorColor="primary"
                                    textColor="primary"
                                    variant="standard"
                                    scrollButtons="auto"
                                    classes={{ root: classes.tabroot, scrollButtons: classes.tabScrollButton }}
                                >
                                    <Tab style={{textTransform: "none", fontSize: "14px"}} label="Request password" icon={<Password />}
                                        classes={{
                                            root: classes.tab,
                                            wrapper: classes.labelWrapped,
                                        }}
                                    />
                                    <Tab style={{textTransform: "none", fontSize: "14px"}} label="Request invitation" icon={<SettingsAccessibility />}
                                        classes={{
                                            root: classes.tab,
                                            wrapper: classes.labelWrapped,
                                        }}
                                    />
                                </Tabs>
                            </Grid>
                            <Grid item xs={12}>

                                {/* Share credentials */}
                                {this.state.taskType === 0 &&
                                <Box className={classes.groupBoxCredentials}>
                                    <Accordion onChange={(e, isExpanded) => {this.handleAccordionChange(e, isExpanded)}}>
                                        <AccordionSummary expandIcon={<ExpandMore />}>
                                            <Grid container spacing={0} justifyContent="space-between" alignItems="center">
                                                <Grid item>
                                                    <Typography variant="body1">{credentialLine} for {this.props.appModel.appName}.</Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Typography variant="button">{authOnlyAccordionHelper}</Typography>
                                                </Grid>
                                            </Grid>
                                        </AccordionSummary>
                                        <AccordionDetails className={classes.groupBoxCredentialsAccordion}>
                                            <Grid container spacing={0} justifyContent="flex-start" alignItems="center">
                                                <Grid item xs={12}>
                                                    <FormControlLabel  
                                                        control={<Checkbox className={classes.aoCheckBox} checked={(this.state.askForEmail === true)} onChange={this.toggleStateValue("askForEmail")} />}
                                                        label="Email"
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormControlLabel  
                                                        control={<Checkbox className={classes.aoCheckBox} checked={(this.state.askForPassword === true)} onChange={this.toggleStateValue("askForPassword")} />}
                                                        label="Password"
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormControlLabel  
                                                        control={<Checkbox className={classes.aoCheckBox} checked={(this.state.askForUsername === true)} onChange={this.toggleStateValue("askForUsername")} />}
                                                        label="Username"
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <FormControlLabel  
                                                        control={<Checkbox className={classes.aoCheckBox} checked={(this.state.askForDomain === true)} onChange={this.toggleStateValue("askForDomain")} />}
                                                        label="Subdomain"
                                                    />
                                                </Grid>
                                                {/*
                                                <Grid item xs={12}>
                                                    <FormControlLabel  
                                                        control={<Checkbox className={classes.aoCheckBox} checked={(this.state.askForPhone === true)} onChange={this.toggleStateValue("askForPhone")} />}
                                                        label="Phone"
                                                    />
                                                </Grid>
                                                */}
                                            </Grid>
                                        </AccordionDetails>
                                    </Accordion>
                                </Box>}

                                {/* Invitation */}
                                {this.state.taskType === 1 &&
                                <Box className={classes.groupBoxInvitation}>
                                    <Grid container spacing={2} justifyContent="flex-start" alignItems="center">
                                        <Grid item xs={12}>
                                            <Grid container spacing={1} justifyContent="flex-start" alignItems="center">
                                                <Grid item xs={12}>
                                                    <Grid item><Typography variant="body1"><b>Ask my customer to invite these emails into {this.props.appModel.appName}</b></Typography></Grid>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={0} justifyContent="flex-start" alignItems="center">
                                                        {theInviteEmails.map(n => {
                                                            inviteEmailIndex++;
                                                            let theIndex = inviteEmailIndex;
                                                            let theEmail = n.email;
                                                            let emailChecked = n.checked;
                                                            return (
                                                                <Grid item xs={12} key={theIndex}>
                                                                    <FormControlLabel  
                                                                        control={<Checkbox className={classes.aoCheckBox} checked={emailChecked} onChange={ () => {this.toggleInviteValue(theInviteEmails, theIndex)} }/>}
                                                                        label={theEmail}
                                                                    />
                                                                </Grid>
                                                            );
                                                        })}
                                                    </Grid>
                                                </Grid>
                                                {/* Add ad hoc email */}
                                                <Grid item>
                                                    <Tooltip placement='bottom' title='Add another email'><Add/></Tooltip>
                                                </Grid>
                                                <Grid item>
                                                    <TextField
                                                        className={classes.adHocEmail}
                                                        value={this.state.adHocEmail}
                                                        onChange={ (e) => {this.setState({adHocEmail: e.target.value})} }
                                                        variant="standard"
                                                    />
                                                </Grid>
                                                <Grid item>
                                                    <Button
                                                        className={classes.adHocButton}
                                                        disabled={! this.isAdHocEmailValid(theInviteEmails)}
                                                        onClick={ () => {this.addAdHocEmail(theInviteEmails)} }
                                                        size='small'
                                                    >
                                                        <Check/>
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Grid container spacing={1} justifyContent="flex-start" alignItems="center">
                                                <Grid item xs={12}>
                                                    <Grid item><Typography variant="body1"><b>Provide instructions</b></Typography></Grid>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        className={classes.instructionText}
                                                        value={this.state.instructionText}
                                                        onChange={ (e) => {this.setState({instructionText: e.target.value})} }
                                                        label="Write instructions"
                                                        variant="filled"
                                                        multiline
                                                        minRows={3}
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Grid container spacing={1} justifyContent="flex-start" alignItems="end">
                                                        {/*<Grid item>
                                                            <TextField
                                                                className={classes.linkName}
                                                                value={this.state.linkName}
                                                                onChange={ (e) => {this.setState({linkName: e.target.value})} }
                                                                label="Link name"
                                                                variant="standard"
                                                            />
                                                        </Grid>*/}
                                                        <Grid item>
                                                            <LinkOutlined className={classes.linkIcon}/>
                                                        </Grid>
                                                        <Grid item>
                                                            <TextField
                                                                className={classes.linkUrl}
                                                                value={this.state.linkUrl}
                                                                label="Link to instructions (e.g. support doc)"
                                                                placeholder="https://"
                                                                onChange={ (e) => {this.setState({linkUrl: e.target.value})} }
                                                                variant="standard"
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>

                                        {/*}
                                        <Grid item xs={12}>
                                            <Grid container spacing={1} justifyContent="flex-start" alignItems="center">
                                                <Grid item xs={12}>
                                                    <Grid item><Typography variant="body1"><b>Write instructions</b></Typography></Grid>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        className={classes.instructionTitle}
                                                        value={this.state.instructionTitle}
                                                        onChange={ (e) => {this.setState({instructionTitle: e.target.value})} }
                                                        label="Instruction title"
                                                        variant="standard"
                                                    />
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <TextField
                                                        className={classes.instructionText}
                                                        value={this.state.instructionText}
                                                        onChange={ (e) => {this.setState({instructionText: e.target.value})} }
                                                        label="Write instructions"
                                                        variant="outlined"
                                                        multiline
                                                        minRows={3}
                                                    />
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        */}
                                    </Grid>
                                </Box>}
                            </Grid>
                        </Grid>
                    </Box>
                </Grid>}

                {/* Nothing to do yet for "I will buy" */}
                {/* thePaymentOrder === 5      */}
            </Grid>
        );
    }
}

ModelPicker.propTypes = {
    auth: PropTypes.object.isRequired,
    appModel: PropTypes.object.isRequired,
};

export default withStyles(styleSheet)(ModelPicker);
